/**
 * CepageSelect — Sélecteur de cépage AOC Champagne avec pastille de couleur.
 *
 * Branché sur `trpc.parcellaire.listerCepages` (référentiel global, ~9 lignes).
 * Voltis est marqué `est_conditionnel = true` côté BDD → on affiche un badge
 * "cond." dans l'option et un soft-warning sous le select quand sélectionné.
 *
 * Volontairement contrôlé (props `value` + `onChange`) pour rester compatible
 * react-hook-form via Controller — éviter les surprises de defaultValues.
 */

import { useQuery } from "@tanstack/react-query";
import { forwardRef, type SelectHTMLAttributes } from "react";

import { trpc } from "../../lib/trpc.js";
import { CepagePastille } from "../../pages/vignoble/CepagePastille.js";

import { inputClasses } from "./Input.js";

interface CepageOption {
  id: string;
  code: string;
  libelle: string;
  couleur: string;
  est_conditionnel: boolean;
}

export interface CepageSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  error?: boolean;
  /** Label optionnel "Sélectionnez un cépage…" en première option. */
  placeholder?: string;
  /** Filtre : exclure une liste d'IDs déjà sélectionnés ailleurs. */
  excludeIds?: ReadonlyArray<string>;
}

/**
 * Hook utilitaire — retourne les cépages depuis le cache TanStack Query.
 * Mis en cache 5 min par défaut (référentiel quasi-immuable).
 */
export function useCepages() {
  return useQuery({
    queryKey: ["parcellaire.listerCepages"],
    queryFn: () => trpc.parcellaire.listerCepages.query(),
    // Référentiel quasi-figé : on évite les refetch agressifs.
    staleTime: 1000 * 60 * 30, // 30 min
  });
}

/**
 * Helper d'affichage : retrouve un cépage par ID dans la liste.
 * Retourne `null` si introuvable (cas Voltis pas encore seedé en V0).
 */
export function findCepage(
  cepages: ReadonlyArray<CepageOption> | undefined,
  cepageId: string | null,
): CepageOption | null {
  if (!cepages || !cepageId) return null;
  return cepages.find((c) => c.id === cepageId) ?? null;
}

export const CepageSelect = forwardRef<HTMLSelectElement, CepageSelectProps>(function CepageSelect(
  { error = false, placeholder, excludeIds, className, ...rest },
  ref,
) {
  const { data: cepages, isLoading } = useCepages();
  const visibles = (cepages ?? []).filter((c) => !excludeIds || !excludeIds.includes(c.id));

  return (
    <select
      ref={ref}
      className={[inputClasses(error), className ?? ""].filter(Boolean).join(" ")}
      disabled={isLoading || rest.disabled}
      {...rest}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {visibles.map((c) => (
        <option key={c.id} value={c.id}>
          {c.libelle}
          {c.est_conditionnel ? " (conditionnel)" : ""}
        </option>
      ))}
    </select>
  );
});

/**
 * Variante "preview" : affiche la pastille colorée du cépage sélectionné
 * sous le select. Utile sur les fiches détaillées pour valider visuellement
 * le choix sans rouvrir le dropdown.
 */
export function CepagePreview({
  cepageId,
  cepages,
}: {
  cepageId: string | null;
  cepages: ReadonlyArray<CepageOption> | undefined;
}) {
  const cepage = findCepage(cepages, cepageId);
  if (!cepage) return null;
  return (
    <div className="mt-2 flex items-center gap-2 text-[12px] text-champagne-700">
      <CepagePastille
        libelle={cepage.libelle}
        couleur={cepage.couleur}
        estConditionnel={cepage.est_conditionnel}
        variant="inline"
      />
      {cepage.est_conditionnel && (
        <span className="text-rust-700">— convention INAO/ODG obligatoire avant plantation</span>
      )}
    </div>
  );
}
